import type { GameAddress } from "@softmax/web-addresses";
import { buildHref } from "@softmax/web-addresses";
import {
  type PreviewFacts,
  type PreviewCopy,
  type PreviewMarqueeItem,
} from "./preview-copy.types";
import { templateCopy } from "./preview-template-copy";
import { derivedItems } from "./preview-cadence";
import { beat0GetStarted, beatCoachIt } from "./preview-beats";
import { curatedCopy } from "./preview-curated-copy";
export function previewCopy(f: PreviewFacts): PreviewCopy {
  const base = templateCopy(f);
  const own = curatedCopy[f.slug];
  const derived = derivedItems(f);
  const withDerived = (items: readonly PreviewMarqueeItem[]) => [
    ...derived,
    ...items,
  ];

  if (!own) return { ...base, marquee: withDerived(base.marquee) };

  return {
    cadencePhrase: base.cadencePhrase,
    cadenceShort: base.cadenceShort,
    ctaLabel: own.ctaLabel ?? base.ctaLabel,
    play: {
      beats: own.beats
        ? [beat0GetStarted(f), ...own.beats(f), beatCoachIt()]
        : base.play.beats,
    },
    marquee: withDerived(own.marquee ?? base.marquee),
    included: {
      description: own.included?.description ?? base.included.description,
      cards: own.included?.cards ?? base.included.cards,
    },
    blurb: own.blurb ?? null,
    closingLine: own.closingLine ?? null,
    docs: curatedDocs({
      slug: f.slug,
      game: f.game,
    }),
    team: own.team ?? null,
    replayChrome: own.replayChrome ?? null,
    rulesLicenseNotice: own.rulesLicenseNotice ?? null,
    replayViewerSize: own.replayViewerSize ?? null,
  };
}
export function curatedDocs({
  slug,
  game,
}: {
  slug: string;
  game: GameAddress;
}): PreviewCopy["docs"] {
  const docs = curatedCopy[slug]?.docs;

  if (!docs) return null;

  return {
    title: docs.title,
    subtitle: docs.subtitle,
    href: buildHref({ name: "game.wikiPage", game, page: [docs.page] }),
  };
}
export function replayStageUnfit(slug: string): boolean {
  return curatedCopy[slug]?.replayStageUnfit === true;
}
function compareVersions({ a, b }: { a: string; b: string }): number {
  const as = a.split(".").map(Number);
  const bs = b.split(".").map(Number);

  for (let i = 0; i < Math.max(as.length, bs.length); i++) {
    const diff = (as[i] ?? 0) - (bs[i] ?? 0);

    if (diff !== 0) return diff;
  }

  return 0;
}
// The recorded episode's upload determines chrome support; today's manifest may name a different viewer bundle.
export function stageChromeFor({
  copy,
  coworldVersion,
}: {
  copy: PreviewCopy;
  coworldVersion: string | null;
}): "controls" | "off" | null {
  const minimums = copy.replayChrome;

  if (!minimums || !coworldVersion) return null;

  if (
    minimums.controls &&
    compareVersions({
      a: coworldVersion,
      b: minimums.controls,
    }) >= 0
  ) {
    return "controls";
  }

  if (
    minimums.off &&
    compareVersions({
      a: coworldVersion,
      b: minimums.off,
    }) >= 0
  ) {
    return "off";
  }

  return null;
}
